import { FC } from "react";
import { INTENT } from "../constants";

/**
 * Join K with P, like in object indexing.
 * Example: "K.P".
 */
type DotJoin<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${"" extends P ? "" : "."}${P}`
    : never
  : never;
// prettier-ignore
/**
 * Gets previous value of any value, up to 20. Defaults to never if greater than 20. Yields never on 0.
 * For instance, Previous[9] === 8, Previous[5] === 4, etc.
 */
type Previous = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...never[]];

/**
 * The nested keys of type T, up to depth D (default = 10). Nested keys are joined by dot(s) (".").
 * This can cause excessively deep type instantiation if not used appropriately, or with the proper depth.
 */
// want to use object explicitly for this, because Record<string, unknown> doesn't work the same
export type Paths<T, D extends number = 10> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number
          ? `${K}` | DotJoin<K, Paths<T[K], Previous[D]>>
          : never;
      }[keyof T]
    : "";

export type Intent = (typeof INTENT)[keyof typeof INTENT];

export type DoNotCare = any;

/**
 * Gets the value of a key path from an object.
 * @example
 * ```ts
 * KeyPathValue<{ a: { b: { c: number } } }, "a.b"> // { c: number }
 * KeyPathValue<{ a: { b: { c: number } } }, "a.b.c"> // number
 * KeyPathValue<{ a: { b: { c: number } } }, "a.b.d"> // never
 * ```
 */
export type KeyPathValue<
  T,
  KeyPath extends string | number
> = KeyPath extends `${infer K}.${infer Rest}`
  ? K extends keyof Required<T>
    ? KeyPathValue<Required<T>[K], Rest>
    : never
  : KeyPath extends keyof Required<T>
    ? T[KeyPath]
    : never;

/**
 * Gets the possible key paths of a type.
 * @generic T - The type to get key paths from.
 * @generic Allowed - The allowed types the key paths point.
 * @example
 * ```ts
 * type ObjectPaths = KeyPaths<{
 *    a: { b?: { n: Array<{ id: string }> } };
 * }>;
 * // "a" | "a.b" | "a.b.n" | `a.b.n.${number}` | `a.b.n.${number}.id`
 * ```
 */
export type DeepKeyPaths<T, Allowed = any> = T extends object
  ? {
      [K in keyof T]-?: K extends string
        ? Required<T>[K] extends Array<infer U>
          ? U[] extends Allowed
            ?
                | `${K}`
                | `${K}.${number}`
                | `${K}.${number}.${KeyPaths<U, Allowed>}`
            : U extends Allowed
              ? `${K}.${number}` | `${K}.${number}.${KeyPaths<U, Allowed>}`
              : `${K}.${number}.${KeyPaths<U, Allowed>}`
          : Required<T>[K] extends object
            ? Required<T>[K] extends Allowed
              ? `${K}` | `${K}.${KeyPaths<T[K], Allowed>}`
              : `${K}.${KeyPaths<T[K], Allowed>}`
            : `${K}`
        : never;
    }[keyof Required<T>]
  : never;

/**
 * Gets the possible key paths of a type.
 * @generic T - The type to get key paths from.
 * @generic Allowed - The allowed types the key paths point.
 * @example
 * ```ts
 * type ObjectPaths = KeyPaths<{
 *    a: { b?: { c: number, n: Array<{ id: string }> } };
 *    d: string;
 * }>;
 * // "a" | "a.b" | "a.b.c" | "a.b.n" | "d"
 * ```
 */
export type KeyPaths<T, Allowed = any> = T extends object
  ? {
      [K in keyof T]-?: K extends string
        ? Required<T>[K] extends DoNotCare[]
          ? never
          : Required<T>[K] extends object
            ? Required<T>[K] extends Allowed
              ? `${K}` | `${K}.${KeyPaths<T[K], Allowed>}`
              : `${K}.${KeyPaths<T[K], Allowed>}`
            : `${K}`
        : never;
    }[keyof Required<T>]
  : never;

/**
 * Unwrap types when viewing in editor.
 * This is useful when you have a type that is made from union/utility/generic types,
 * and you want to see the actual properties on the resulting type.
 * @example
 * say we have a utility type like:
 * ```ts
 * type SomeUtility<T> = T & { someKey: string };
 * ```
 * that adds a key to the type. When you use it like:
 * ```ts
 * type SomeType = SomeUtility<{ a: number }>;
 * ```
 * viewing `SomeType` in your editor will show:
 * ```ts
 * SomeUtility<{ a: number }>
 * ```
 * When we use `Prettify<T>`:
 * ```ts
 * type SomeType = Prettify<SomeUtility<{ a: number }>>;
 * ```
 * now, viewing `SomeType` in your editor will show:
 * ```ts
 * { a: number, someKey: string }
 * ```
 *
 */
export type Prettify<T> =
  T extends Record<any, any>
    ? {
        [K in keyof T]: T[K];
      } & {}
    : T;

export type WithRequired<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Rename a key on an object.
 * @example
 * ```ts
 * type A = { id: string; render: (item) => ReactNode };
 * type B = Rename<"render", "children", A>;
 * // B is now: { id: string; children: (item) => ReactNode }
 * ```
 */
export type RenameKey<
  OldKey extends keyof T,
  NewKey extends string,
  T extends { [key in string]: any }
> = Prettify<
  T extends { [key in OldKey]: infer Value }
    ? { [key in NewKey]: Value } & Omit<T, OldKey>
    : T extends { [key in OldKey]?: infer Value }
      ? { [key in NewKey]?: Value } & Omit<T, OldKey>
      : never
>;

/**
 * Adds a displayName to a React functional component, for better debugging and documentation.
 * @param component - The React functional component to add the displayName to.
 * @param displayName - The displayName to add to the component.
 * @returns The same component with the displayName added.
 * @example
 * ```ts
 * function MySubComponent() { ... };
 * MyComponent.Sub = withDisplayName(MySubComponent, "MyComponent.Sub");
 * ```
 */
export function withDisplayName<C extends FC>(
  component: C,
  displayName?: string
) {
  if (displayName) {
    component.displayName = displayName;
  } else if (!component.displayName && component.name) {
    component.displayName = component.name;
  }
  return component as C & { displayName?: string };
}
